/**
 * Recruiter attention helpers for My Calls.
 *
 * The Reminders card lists Not in ATS + AI flags. Missed and not-answered
 * stay on their own cards. Rows come from GET /api/exotel/call-analytics.
 */

import {
  isInboundDirection,
  normalizeDirection,
  hasInterestStatusMismatch,
  getAiNegativeVerdictLabel,
  getCandidateStatusFromRow,
  getWhyProfileShared,
  parseAnalysis,
  pickCallRowDate,
  pickExplicitCallRowDate,
  parseCallRowTimestamp,
  formatHistoryDateIst,
  callRowSortTimestamp,
} from '@/utils/call-analysis-helpers'
import { isDialableIndianPhone, resolveCallRowPhone } from '@/utils/exotel-call-helpers'

const QUEUED_STATUS_KEYS = new Set([
  'queued',
  'scheduled',
  'pending',
  'initiated',
  'calling',
  'ringing',
  'in_progress',
  'in-progress',
  'inprogress',
])

const NO_ANSWER_STATUSES = new Set([
  'no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered',
  'not_answered', 'not_rang', 'not-rang', 'not connected', 'not-connected', 'notconnected',
  'missed',
])

/** Inbound while unavailable: ATS writes Status=not_rang and may omit Direction. */
const START_CALLING_SKIP_STATUSES = new Set(['not_rang', 'not-rang', 'notrang'])

/** Inbound-skip statuses the webhook may persist when Direction is empty. */
const INBOUND_SKIP_LIKE_STATUSES = new Set([
  ...START_CALLING_SKIP_STATUSES,
  'missed',
  'missed-call',
  'missed_call',
  'not-connected',
  'not_connected',
  'notconnected',
  'not connected',
])

const FAILED_STATUSES = new Set([
  'failed', 'temporaryfailed', 'temporary-failed', 'temporary_failed', 'temporary failed',
])

const CONNECTED_STATUSES = new Set(['answered', 'completed', 'in-progress'])

const ATTENTION_BADGE = {
  not_added: 'Not in ATS',
  flagged: 'Flagged',
  not_answered: 'Not Answered',
  missed: 'Missed',
}

function statusKey(row) {
  if (!row || typeof row !== 'object') return ''
  return String(row.status || row.call_status || '').toLowerCase().trim()
}

function parseJsonObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value
  if (typeof value !== 'string' || !value.trim()) return {}
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (e) {
    return {}
  }
}

function rawPayloadOf(row) {
  if (!row || typeof row !== 'object') return {}
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  return parseJsonObject(raw.raw_payload || row.raw_payload)
}

function contextOf(row) {
  return rawPayloadOf(row).context_resolution || {}
}

function isEmptyId(value) {
  if (value == null || value === false) return true
  const s = String(value).trim()
  return !s || s === '0' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined'
}

function pickStatus(row) {
  if (!row || typeof row !== 'object') return ''
  if (row._rawStatus) return String(row._rawStatus).toLowerCase().trim()
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  return String(raw.status || raw.Status || row.status || '').toLowerCase().trim()
}

function pickDirection(row) {
  if (!row || typeof row !== 'object') return ''
  if (row._rawDirection) return String(row._rawDirection).toLowerCase()
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  const payload = rawPayloadOf(row)
  return String(
    raw.direction
    || raw.Direction
    || payload.direction
    || payload.Direction
    || payload.webhook_response?.data?.Direction
    || payload.webhook_response?.data?.direction
    || row.direction
    || '',
  ).toLowerCase()
}

function isPlaceholderDisplayDate(value) {
  if (value == null) return true
  const s = String(value).trim()
  return !s
    || s === '—'
    || s === '--'
    || s === '–'
    || s === '-'
    || s.toLowerCase() === 'invalid date'
}

/**
 * Same IST cell as History / Not Answered / Missed: `8 Sept, 2:47 pm IST`.
 * Prefers an already-formatted History string, then the call timestamp.
 */
function formatAttentionDateIst(row, payload) {
  const existing = row && row.date
  if (existing && !isPlaceholderDisplayDate(existing) && /IST/i.test(String(existing))) {
    return String(existing).trim()
  }
  const rawDate = (row && row._rawDate)
    || (row && row._ts)
    || pickExplicitCallRowDate(row)
    || pickExplicitCallRowDate(payload)
    || pickCallRowDate(row)
    || pickCallRowDate(payload)
    || null
  const parts = formatHistoryDateIst(rawDate)
  if (!parts.date || parts.date === '—') return '—'
  return parts.time ? `${parts.date}, ${parts.time}` : parts.date
}

export function getIstTodayStr(now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now)
  } catch (e) {
    return now.toISOString().slice(0, 10)
  }
}

export function isNoAnswerExotelStatus(status) {
  return NO_ANSWER_STATUSES.has(String(status || '').toLowerCase().trim())
}

export function isFailedExotelStatus(status) {
  return FAILED_STATUSES.has(String(status || '').toLowerCase().trim())
}

export function isConnectedExotelStatus(status) {
  return CONNECTED_STATUSES.has(String(status || '').toLowerCase().trim())
}

/**
 * True when the voice call has no ATS candidate (called but not added).
 * Detected from candidate_id / candidateId / context_resolution, or explicit
 * ats_added=false / not_added=true if the API ever sends those.
 */
export function isMissingAtsCandidate(row) {
  if (!row || typeof row !== 'object') return false
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  if (raw.ats_added === false || raw.atsAdded === false || row.ats_added === false) return true
  if (raw.not_added === true || raw.notAdded === true || row.not_added === true) return true
  // Mapped Call History rows use `candidateId` the same way as the "Not in ATS" badge.
  if (Object.prototype.hasOwnProperty.call(row, 'candidateId')) {
    return isEmptyId(row.candidateId)
  }
  const ctx = contextOf(row)
  const id = raw.candidate_id || raw.candidateId || ctx.candidate_id || row.candidate_id
  return isEmptyId(id)
}

export function isStartCallingSkipStatus(status) {
  return START_CALLING_SKIP_STATUSES.has(String(status || '').toLowerCase().trim())
}

function truthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function isExplicitOutboundDirection(dir) {
  const d = String(dir || '').toLowerCase()
  return d.includes('outbound') || d === 'outgoing'
}

function hasInboundSkipFlag(row) {
  if (!row || typeof row !== 'object') return false
  if (row._callsAvailableSkip) return true
  const payload = rawPayloadOf(row)
  if (truthyFlag(payload.calls_available_skip) || truthyFlag(payload.callsAvailableSkip)) return true
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  if (truthyFlag(raw.calls_available_skip) || truthyFlag(raw.callsAvailableSkip)) return true
  if (truthyFlag(row.calls_available_skip) || truthyFlag(row.callsAvailableSkip)) return true
  const gate = payload.inbound_gate || payload.inboundGate || raw.inbound_gate || null
  if (gate && typeof gate === 'object') {
    if (truthyFlag(gate.applied) && gate.calls_available === false) return true
    if (gate.reason === 'paused' || isStartCallingSkipStatus(gate.status)) return true
  }
  return false
}

function hasCallsAvailableFalse(row) {
  if (!row || typeof row !== 'object') return false
  const payload = rawPayloadOf(row)
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  return row.calls_available === false
    || payload.calls_available === false
    || raw.calls_available === false
}

/**
 * Inbound that hit the DID while the recruiter was unavailable.
 * Contract: raw_payload.calls_available_skip, calls_available: false, inbound_gate,
 * and/or Status=not_rang. Explicit outbound (including blocked 409 clicks) is never this.
 */
export function isReceivedWhileAway(row) {
  if (!row || typeof row !== 'object') return false
  const status = pickStatus(row)
  if (isConnectedExotelStatus(status)) return false
  const dir = pickDirection(row)
  if (isExplicitOutboundDirection(dir)) return false
  if (hasInboundSkipFlag(row)) return true
  if (hasCallsAvailableFalse(row)) return true
  return isStartCallingSkipStatus(status)
}

/**
 * Inbound skipped because the recruiter was unavailable.
 * Direction may be missing — still treat as inbound missed.
 */
export function isStartCallingUnavailableCall(row) {
  return isReceivedWhileAway(row)
}

export function isOutboundNotAnswered(row) {
  if (!row || typeof row !== 'object') return false
  if (isReceivedWhileAway(row)) return false
  const dir = pickDirection(row)
  const status = pickStatus(row)
  return !isInboundDirection(dir) && isNoAnswerExotelStatus(status)
}

export function isInboundMissed(row) {
  if (!row || typeof row !== 'object') return false
  const status = pickStatus(row)
  if (isConnectedExotelStatus(status)) return false
  const dir = pickDirection(row)
  // Blocked outbound 409 never hits Exotel; do not treat outbound rows as missed.
  if (isExplicitOutboundDirection(dir)) return false
  if (isReceivedWhileAway(row)) return true
  if (isInboundDirection(dir)) return true
  // Webhook may omit Direction and use missed / not-connected / not_rang.
  return !dir && INBOUND_SKIP_LIKE_STATUSES.has(status)
}

/** Last-10 Indian mobile — same unique key as Call Analytics KPI / Missed cards. */
function last10Phone(raw) {
  if (raw == null || raw === '' || raw === '—') return ''
  let digits = String(raw).replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2)
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
  if (!digits) return ''
  return digits.length >= 10 ? digits.slice(-10) : digits
}

function isUsableCallerId(value) {
  if (value == null || value === false) return false
  const s = String(value).trim()
  if (!s || s === '0') return false
  const lower = s.toLowerCase()
  return lower !== 'null' && lower !== 'undefined'
}

/**
 * Unique caller key: last-10 phone, else candidate_id, else sid / rowKey.
 * Phoneless inbound-while-away rows stay as themselves.
 */
export function callerUniqueKey(row) {
  if (!row || typeof row !== 'object') return null
  const phone = last10Phone(
    row.phone
    || row.contactNo
    || row.contact_no
    || row.candidate_contact_no,
  )
  if (phone) return `p:${phone}`
  const cid = row.candidateId != null && row.candidateId !== ''
    ? row.candidateId
    : row.candidate_id
  if (isUsableCallerId(cid)) return `c:${cid}`
  if (row.sid) return `s:${row.sid}`
  if (row.rowKey) return `r:${row.rowKey}`
  if (row.id != null && row.id !== '') return `id:${row.id}`
  return null
}

function isNewerCall(next, prev, timeFn) {
  if (!prev) return true
  const tn = timeFn(next)
  const tp = timeFn(prev)
  if (tn !== tp) return tn > tp
  const idn = Number(next && (next.callDbId != null && next.callDbId !== '' ? next.callDbId : next.id))
  const idp = Number(prev && (prev.callDbId != null && prev.callDbId !== '' ? prev.callDbId : prev.id))
  if (Number.isFinite(idn) && Number.isFinite(idp) && idn !== idp) return idn > idp
  return false
}

/**
 * Latest-call outcome for one row (not yet unique-by-caller).
 * answered → connected; inbound not-connected → missed; outbound not-connected → not_answered.
 */
export function latestCallOutcome(row) {
  if (!row || typeof row !== 'object') return 'other'
  const status = pickStatus(row)
  if (isConnectedExotelStatus(status)) return 'connected'
  if (isInboundMissed(row)) return 'missed'
  if (isOutboundNotAnswered(row)) return 'not_answered'
  if (isFailedExotelStatus(status)) return 'failed'
  return 'other'
}

/**
 * Per unique caller, the most recent call decides NA vs Missed vs Connected.
 * A person is never in both Not Answered and Missed.
 * Connected stays after a later inbound miss (candidate called back, recruiter missed).
 *
 * @param {Array} rows — live filtered call-analytics / mapped History rows
 * @param {{ keyFn?: Function, timeFn?: Function }} [options]
 * @returns {{
 *   totalByCaller: Map,
 *   connectedByCaller: Map,
 *   notAnsweredByCaller: Map,
 *   missedByCaller: Map,
 *   failedByCaller: Map,
 * }}
 */
export function latestCallerStateBuckets(rows, options = {}) {
  const keyFn = options.keyFn || callerUniqueKey
  const timeFn = options.timeFn || callRowSortTimestamp
  const list = Array.isArray(rows) ? rows : []
  const byCaller = new Map()
  const leftovers = []

  for (let i = 0; i < list.length; i += 1) {
    const c = list[i]
    const key = keyFn(c, i)
    if (!key) {
      leftovers.push(c)
      continue
    }
    let rec = byCaller.get(key)
    if (!rec) {
      rec = { latest: c, everConnected: false, newestConnected: null }
      byCaller.set(key, rec)
    } else if (isNewerCall(c, rec.latest, timeFn)) {
      rec.latest = c
    }
    if (latestCallOutcome(c) === 'connected') {
      rec.everConnected = true
      if (isNewerCall(c, rec.newestConnected, timeFn)) rec.newestConnected = c
    }
  }

  const totalByCaller = new Map()
  const connectedByCaller = new Map()
  const notAnsweredByCaller = new Map()
  const missedByCaller = new Map()
  const failedByCaller = new Map()

  const classify = (key, rec) => {
    const latest = rec.latest
    totalByCaller.set(key, latest)
    const bucket = latestCallOutcome(latest)
    if (bucket === 'connected' || rec.everConnected) {
      connectedByCaller.set(key, rec.newestConnected || latest)
    }
    if (bucket === 'not_answered') notAnsweredByCaller.set(key, latest)
    else if (bucket === 'missed') missedByCaller.set(key, latest)
    else if (bucket === 'failed') failedByCaller.set(key, latest)
  }

  byCaller.forEach((rec, key) => classify(key, rec))
  leftovers.forEach((c, i) => {
    const connected = latestCallOutcome(c) === 'connected'
    classify(`leftover:${i}`, {
      latest: c,
      everConnected: connected,
      newestConnected: connected ? c : null,
    })
  })

  return {
    totalByCaller,
    connectedByCaller,
    notAnsweredByCaller,
    missedByCaller,
    failedByCaller,
  }
}

/**
 * Primary attention reason for a call row.
 * Inbound missed (including received-while-away / not-in-ATS) feeds the Missed card.
 * Output / Reminders still uses not_added + flagged for outbound “called but not added”.
 */
export function attentionKind(row) {
  if (!row || typeof row !== 'object') return null
  if (isInboundMissed(row)) return 'missed'
  if (isMissingAtsCandidate(row)) return 'not_added'
  if (hasInterestStatusMismatch(row)) return 'flagged'
  if (isOutboundNotAnswered(row)) return 'not_answered'
  return null
}

/**
 * Compact Output-column badge for a history row.
 * @param {object|null|undefined} row
 * @returns {{ kind: string, label: string, variant: string }|null}
 */
export function getOutputBadge(row) {
  const kind = attentionKind(row)
  if (!kind) return null
  return {
    kind,
    label: ATTENTION_BADGE[kind] || 'Needs attention',
    variant: (kind === 'not_added' || kind === 'flagged') ? 'light-danger' : 'light-warning',
  }
}

export function isAttentionExotelCall(row) {
  return attentionKind(row) != null
}

export function mapExotelAttentionRow(rawCall) {
  const raw = rawCall && typeof rawCall === 'object' ? rawCall : {}
  const payload = rawPayloadOf(raw)
  const ctx = payload.context_resolution || {}
  let rawDir = pickDirection(raw)
  const rawStatus = pickStatus(raw)
  if (!isInboundDirection(rawDir) && isInboundMissed(raw)) {
    rawDir = 'inbound'
  }
  const kind = attentionKind(raw)

  const callForPhone = {
    ...raw,
    From: raw.from_number || raw.From || raw.from,
    To: raw.to_number || raw.To || raw.to,
    from: raw.from_number || raw.from,
    to: raw.to_number || raw.to,
    phone: raw.phone || raw.candidate_contact_no || raw.contact_no,
  }
  const phoneLocal = resolveCallRowPhone(callForPhone, payload, ctx, rawDir)
  const phone = phoneLocal || (raw.phone && raw.phone !== '—' ? raw.phone : '') || ''

  const candidateId = raw.candidateId != null && raw.candidateId !== ''
    ? raw.candidateId
    : (raw.candidate_id || ctx.candidate_id || null)
  const safeCandidateId = isEmptyId(candidateId) ? null : candidateId
  const candidateName = raw.candidate_name || raw.candidateName || raw.candidate || ctx.candidate_name || ''
  const recruiterId = raw.recruiter || raw.Recruiter || raw._rawRecruiter || ''
  const recruiterName = raw.recruiter_name || raw._recruiterName || ''
  const rawDate = raw._rawDate
    || (raw._ts ? raw._ts : null)
    || pickExplicitCallRowDate(raw)
    || pickExplicitCallRowDate(payload)
    || pickCallRowDate(raw)
    || pickCallRowDate(payload)
    || null
  const sid = String(raw.sid || raw.Sid || raw.CallSid || raw.callSid || '')
  const date = formatAttentionDateIst(raw, payload)
  const parsedTs = parseCallRowTimestamp(rawDate)
  const ts = (typeof raw._ts === 'number' && raw._ts)
    || (parsedTs && !Number.isNaN(parsedTs.getTime()) ? parsedTs.getTime() : 0)
  const safeName = candidateName && candidateName !== '—' ? candidateName : ''
  const title = safeName || phone || recruiterName || '—'

  return {
    id: raw.id || raw.call_id || sid || null,
    sid,
    rowKey: raw.rowKey || `${safeCandidateId || ''}_${raw.job_id || raw.jobId || ''}_${sid}`,
    date,
    phone: phone || '—',
    phoneDialable: isDialableIndianPhone(phone),
    candidate: candidateName && candidateName !== '—' ? candidateName : '',
    candidateId: safeCandidateId,
    jobId: raw.job_id || raw.jobId || ctx.job_id || null,
    _rawRecruiter: recruiterId,
    _recruiterName: recruiterName,
    _rawStatus: rawStatus,
    _rawDirection: rawDir,
    _callsAvailableSkip: isReceivedWhileAway(raw),
    _rawDate: rawDate,
    _ts: ts,
    direction: normalizeDirection(rawDir),
    status: kind ? ATTENTION_BADGE[kind] : (raw.status || 'unknown'),
    attentionKind: kind,
    attentionBadge: kind ? ATTENTION_BADGE[kind] : '',
    candidateLabel: title,
    timeLabel: date,
    contactNo: phone || '',
    recruiterName,
    candidateStatus: getCandidateStatusFromRow(raw) || '',
    aiVerdict: kind === 'flagged' ? (getAiNegativeVerdictLabel(parseAnalysis(raw)) || 'Not interested / Not suitable') : '',
    whyShared: kind === 'flagged' ? getWhyProfileShared(raw) : '',
  }
}

function sortNewest(a, b) {
  return callRowSortTimestamp(b) - callRowSortTimestamp(a)
}

/**
 * Newest attention calls first. Dedupes by sid / rowKey.
 * @param {Array} calls — raw call-analytics rows or mapped rows
 * @returns {Array} mapExotelAttentionRow output
 */
function attentionDedupeKey(item) {
  const sid = String((item && item.sid) || '').trim()
  if (sid) return `s:${sid}`
  const id = item && item.id != null && item.id !== '' ? String(item.id) : ''
  if (id) return `id:${id}`
  return [
    'f',
    (item && item.phone) || '',
    (item && (item._rawDate || item.date)) || '',
    (item && item._rawStatus) || '',
    (item && item._rawDirection) || '',
    (item && item._rawRecruiter) || '',
  ].join(':')
}

export function filterAttentionExotelCalls(calls) {
  const list = Array.isArray(calls) ? calls : []
  const seen = new Set()
  const mapped = []
  list.forEach((row) => {
    if (!isAttentionExotelCall(row)) return
    const item = mapExotelAttentionRow(row)
    const key = attentionDedupeKey(item)
    if (seen.has(key)) return
    seen.add(key)
    mapped.push(item)
  })
  return mapped.sort(sortNewest)
}

export function partitionAttentionExotelCalls(calls) {
  const list = Array.isArray(calls) ? calls : []
  const all = filterAttentionExotelCalls(list)
  const states = latestCallerStateBuckets(list)
  const naKeys = new Set(states.notAnsweredByCaller.keys())
  const missedKeys = new Set(states.missedByCaller.keys())

  const newestAllowed = (items, allowedKeys) => {
    const best = new Map()
    items.forEach((c) => {
      const key = callerUniqueKey(c)
      if (!key || !allowedKeys.has(key)) return
      const prev = best.get(key)
      if (!prev || callRowSortTimestamp(c) > callRowSortTimestamp(prev)) best.set(key, c)
    })
    return Array.from(best.values()).sort(sortNewest)
  }

  const notAdded = all.filter(c => c.attentionKind === 'not_added' || (
    isMissingAtsCandidate(c) && !isInboundMissed(c)
  ))
  const flagged = all.filter(c => c.attentionKind === 'flagged')
  return {
    // Reminders / Output card: exclude missed + not-answered
    items: all.filter(c => c.attentionKind === 'not_added' || c.attentionKind === 'flagged'),
    // Latest-call wins: later inbound miss / outbound NA / answer moves the person
    notAnswered: newestAllowed(
      all.filter(c => c.attentionKind === 'not_answered'),
      naKeys,
    ),
    missed: newestAllowed(
      all.filter(c => c.attentionKind === 'missed' || isInboundMissed(c)),
      missedKeys,
    ),
    notAdded,
    flagged,
  }
}

/**
 * True when an AI call still needs recruiter attention
 * (queued / in-flight / not answered / missed).
 * Kept for job-level AI calling; My Calls Reminders uses Exotel helpers above.
 * @param {Object} row — raw or normalizeAiCallRow output
 */
export function isAttentionAiCall(row) {
  if (!row || typeof row !== 'object') return false
  const status = statusKey(row)
  if (NO_ANSWER_STATUSES.has(status)) return true
  if (QUEUED_STATUS_KEYS.has(status)) return true

  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  const outcomes = raw.outcomes || raw.feedback || raw.call_outcomes || null
  if (outcomes && typeof outcomes === 'object' && !Array.isArray(outcomes)) {
    const cs = String(
      outcomes.call_status
      || outcomes.callStatus
      || outcomes.result
      || '',
    ).toLowerCase().trim()
    if (QUEUED_STATUS_KEYS.has(cs) || NO_ANSWER_STATUSES.has(cs)) return true
  }

  return false
}

/**
 * Newest attention AI calls first.
 * @param {Array} calls — normalizeAiCallRow output
 * @returns {Array}
 */
export function filterAttentionAiCalls(calls) {
  const list = Array.isArray(calls) ? calls.filter(isAttentionAiCall) : []
  return list.slice().sort((a, b) => {
    const ta = a && a.createdAt ? new Date(a.createdAt).getTime() : 0
    const tb = b && b.createdAt ? new Date(b.createdAt).getTime() : 0
    return tb - ta
  })
}

export default {
  isMissingAtsCandidate,
  isOutboundNotAnswered,
  isStartCallingSkipStatus,
  isStartCallingUnavailableCall,
  isReceivedWhileAway,
  isInboundMissed,
  callerUniqueKey,
  latestCallOutcome,
  latestCallerStateBuckets,
  attentionKind,
  getOutputBadge,
  isAttentionExotelCall,
  mapExotelAttentionRow,
  filterAttentionExotelCalls,
  partitionAttentionExotelCalls,
  isAttentionAiCall,
  filterAttentionAiCalls,
  getIstTodayStr,
}
